import { DocumentCardData } from "@hub/models/document/document-card-data";
import { Layouts } from "@hub/values/layout-values";
import Fuse from "fuse.js";
import remove from "lodash/remove";
import indexOf from "lodash/indexOf";
import { NextRouter } from "next/router";
import { ParsedUrlQueryInput } from "querystring";

interface HubPageMetadataWithSearch extends DocumentCardData {
  all: string;
}

interface FilterResultsConfig {
  content: DocumentCardData[] | null;
  type: string[];
  framework: string[];
  language: string[];
  topic: string[];
  searchTerm: string;
}

const inlineMetadataSearchableValues = (
  contentCardConfig: DocumentCardData
) => {
  const newObject: HubPageMetadataWithSearch = {
    ...contentCardConfig,
    all: "",
  };

  newObject.all = `${contentCardConfig.title} ${
    contentCardConfig.description
  } ${contentCardConfig.keywords.join(" ")} ${contentCardConfig.framework} ${
    contentCardConfig.language
  } ${contentCardConfig.type} ${contentCardConfig.vertical} ${
    contentCardConfig.path
  }`;

  return newObject;
};

export const loadFiltersOnUrl = async (
  router: NextRouter,
  pathname: string,
  query: ParsedUrlQueryInput
): Promise<void> => {
  await router.push(
    {
      pathname,
      query,
    },
    undefined,
    { shallow: true }
  );
};

const handleSearchTermChange = (
  searchTerm: string,
  filteredResults: DocumentCardData[]
) => {
  const options = {
    isCaseSensitive: false,
    includeScore: true,
    findAllMatches: true,
    useExtendedSearch: true,
    keys: ["all"],
  };

  const terms = searchTerm
    .trim()
    .split(" ")
    .map((term) => `'${term}`)
    .join(" ");

  const enhancedResults = filteredResults.map((s: DocumentCardData) =>
    inlineMetadataSearchableValues(s)
  );

  const fuse = new Fuse(enhancedResults, options);

  return fuse.search(terms).map((result) => {
    return result.item;
  });
};

export const filterContent = (config: FilterResultsConfig) => {
  const { content, type, framework, language, searchTerm, topic } = config;

  if (!content) {
    return null;
  }

  let filteredResults = content.filter((item) => {
    const typeSet = new Set(type);
    const frameworkSet = new Set(framework);
    const languageSet = new Set(language);
    const topicSet = new Set(topic);

    if (item.layout === Layouts.GUIDE) {
      if (!(item.topic && item.type && item.framework && item.languages)) {
        return false;
      }
    }

    if (item.layout === Layouts.SAMPLE) {
      if (!(item.type && item.framework && item.languages)) {
        return false;
      }
    }

    if (item.topic && topicSet.size > 0 && !topicSet.has(item.topic)) {
      return false;
    }

    if (item.type && typeSet.size > 0 && !typeSet.has(item.type)) {
      return false;
    }

    if (
      item.framework &&
      frameworkSet.size > 0 &&
      !frameworkSet.has(item.framework)
    ) {
      return false;
    }

    if (
      item.languages.length > 0 &&
      languageSet.size > 0 &&
      item.languages.filter((language) => languageSet.has(language)).length < 1
    ) {
      return false;
    }

    return !(
      item.language &&
      languageSet.size > 0 &&
      !languageSet.has(item.language)
    );
  });

  if (searchTerm) {
    filteredResults = handleSearchTermChange(searchTerm, filteredResults);
  }

  return filteredResults;
};

export const getRouterQueryWithSelectedFilters = (filterConfig: {
  enabledFilterCategories: {
    topic: boolean;
    type: boolean;
    framework: boolean;
    language: boolean;
  };
  filterCategoryKey: string;
  selectedFilterValue: string;
  filterValues: {
    [index: string]: string[] | undefined;
    topic?: string[];
    type: string[];
    framework: string[];
    language: string[];
  };
}): ParsedUrlQueryInput | null => {
  const {
    enabledFilterCategories,
    filterCategoryKey,
    filterValues,
    selectedFilterValue,
  } = filterConfig;

  const filterValueIndex = indexOf(
    filterValues[filterCategoryKey],
    selectedFilterValue
  );

  filterValueIndex > -1 && filterValues[filterCategoryKey]
    ? remove(
        filterValues[filterCategoryKey]!,
        (item) => item === filterValues[filterCategoryKey]![filterValueIndex]
      )
    : filterValues[filterCategoryKey]!.push(selectedFilterValue);

  const routerQuery: ParsedUrlQueryInput = {};

  if (
    enabledFilterCategories.topic &&
    filterValues.topic &&
    filterValues.topic.length > 0
  ) {
    routerQuery.topic = filterValues.topic.join(",");
  }

  if (enabledFilterCategories.type && filterValues.type.length > 0) {
    routerQuery.type = filterValues.type.join(",");
  }

  if (enabledFilterCategories.framework && filterValues.framework.length > 0) {
    routerQuery.framework = filterValues.framework.join(",");
  }

  if (enabledFilterCategories.language && filterValues.language.length > 0) {
    routerQuery.language = filterValues.language.join(",");
  }

  return routerQuery;
};
